import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {Equipamento, EquipamentoResponse} from '../../models/equipamentos/equipamento';
import {Resposta} from '../../models/resposta';
import {EquipamentoFiltro} from '../../models/equipamentos/equipamento-filtro';
import {catchError, tap} from 'rxjs/operators';
import {of, Subject, throwError} from 'rxjs';
import {Armazem} from '../../models/artigos/armazem';
import {EqEstado} from '../../models/equipamentos/eq-estado';
import {Documentos} from '../../models/financas/documentos/documentos';
import {LoginService} from '@services/aplicacao-service/login.service';
import {FiltroPreferencia} from '../../models/equipamentos/filtro-preferencia';
import {HttpTransportType, HubConnection, HubConnectionBuilder, LogLevel} from '@microsoft/signalr';
import { MenuDataService } from '@services/aplicacao-service/data/menu-data.service';

@Injectable({
    providedIn: 'root'
})
export class EquipamentoService {
    url = `${environment.apiEquipamentos}Equipamentos`;
    private connection!: HubConnection;
    private equipmentUpdatedSubject = new Subject<void>();
    equipamentosUpdated$ = this.equipmentUpdatedSubject.asObservable();

    constructor(private http: HttpClient, private loginService: LoginService, private menuStore: MenuDataService) {
        this.menuStore.getListaMenus().subscribe((menus) => {
          let menuEquipamento = menus.findIndex((m) => m.codMenu === "EQUIPA") !== -1;            
            if(menuEquipamento){
                this.connect();
            }
        });
    }

    connect() {
        this.connection = new HubConnectionBuilder()
            .configureLogging(LogLevel.Information)
            .withAutomaticReconnect()
            .withUrl(`${environment.equipamentosHub}equipamentos`, {
                withCredentials: false,
                transport: HttpTransportType.WebSockets
            })
            .build();
        this.connection.on('UpdateEquipamentos', () => this.equipmentUpdatedSubject.next());
        this.connection.start().then(() => console.log('SignalR connection started'));
    }

    updateEquipamentos() {
        return this.connection.invoke<void>('LoopbackUpdateEquipamentos');
    }

    postEquipamento(modelo: Equipamento) {
        return this.http.post<Resposta<Equipamento>>(this.url, modelo);
    }

    getEquipamentos() {
        return this.http.get<Resposta<EquipamentoResponse[]>>(this.url);
    }

    getEquipamento(id: number) {
        return this.http.get<Resposta<EquipamentoResponse>>(`${this.url}/${id}`,
            {params: {idUtilizador: this.loginService.getUtilizadorSessao()?.idUtilizador}});
    }

    filtrarEquipamentos(filtro: EquipamentoFiltro) {
        filtro.idUtilizador = this.loginService.getUtilizadorSessao()?.idUtilizador;
        return this.http.post<Resposta<EquipamentoResponse[]>>(`${this.url}/filtrar`, filtro)
            .pipe(catchError(err => {
                console.error(err);
                return err.status === 404 ?
                    of({objecto: [], statusCode: err.status, mensagem: ''} as Resposta<EquipamentoResponse[]>) : throwError(err);
            }));
    }

    putEquipamento(modelo: Equipamento) {
        return this.http.post<Resposta<Equipamento>>(`${this.url}/${modelo.id}`, modelo);
    }

    deleteEquipamento(id: number) {
        return this.http.post<Resposta<Equipamento>>(`${this.url}/remover/${id}`, {});
    }

    getArmazens() {
        return this.http.get<Resposta<Armazem[]>>(`${this.url}/armazens`);
    }

    getCombustiveis() {
        return this.http.get<Resposta<EqEstado[]>>(`${this.url}/combustiveis`);
    }

    getEmpresas() {
        return this.http.get<Resposta<any[]>>(`${this.url}/empresas`);
    }

    putEquipamentoLote(req: any) {
        return this.http.post<Resposta<Equipamento>>(`${this.url}/lote`, req);
    }

    getDocumentos(id: number) {
        return this.http.get<Resposta<Documentos[]>>(`${this.url}/${id}/documentos`,
            {params: {idUtilizador: this.loginService.getUtilizadorSessao()?.idUtilizador}});
    }

    imprimirDocumentoEntidade(id: number, code: string) {
        return this.http.get<Resposta<string>>(`${this.url}/entidades/${id}/documentos`, {params: {code}});
    }

    filtrarPermissoes(filtro: any) {
        return this.http.post<Resposta<Equipamento>>(`${this.url}/permissoes/filtrar`, filtro);
    }

    getPreferencias(filtro: any) {
        return this.http.post<Resposta<FiltroPreferencia[]>>(`${this.url}/preferencias/filtrar`, filtro);
    }

    patchEntidade(req: { dataFim: Date; id: number; dataInicio: Date }) {
        return this.http.post<Resposta<any>>(`${this.url}/entidades`, req);
    }
}
